import React from 'react';
import { Box, Button, SvgIcon, Typography } from '@mui/material';
import { borderRadius, customSizes, palette, spaces } from '../../assets/styles/themes/tokens';
import { useTranslation } from 'react-i18next';
import getMuiIcon from '../../utils/getMuiIcon';
import { STRIPE_SCHOOL_LICENSE_DISPLAY } from '../../constants/stripePlanBenefits';

interface StripeSchoolLicenseCtaProps {
  onContact: () => void;
}

// Rightmost column of the Stripe upgrade row. Always rendered (independent of
// the user's plan or how many upgrade options come back), so the section still
// has an entry point for school-license enquiries. Duplicated from the public
// pricing-page school card to keep the Stripe path independent.
const StripeSchoolLicenseCta: React.FC<StripeSchoolLicenseCtaProps> = ({ onContact }) => {
  const { t } = useTranslation();
  const display = STRIPE_SCHOOL_LICENSE_DISPLAY;

  return (
    <Box
      data-testid='stripe-school-license-cta'
      sx={{
        width: customSizes.pricingPackage.width,
        display: 'flex',
        flexDirection: 'column',
        gap: spaces.xl,
        border: `1px solid ${palette.elevation.outlined}`,
        borderRadius: borderRadius.xl,
        backgroundColor: palette.common.white,
        position: 'relative',
        padding: spaces.lg,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-end',
          height: '80px',
        }}
      >
        <Typography
          variant='h4'
          color={palette.text.primary}
          sx={{ fontWeight: 600 }}
        >
          {t(display.packageName)}
        </Typography>
        <Typography variant='body2' color={palette.text.secondary}>
          {t(display.description)}
        </Typography>
      </Box>
      <Box>
        <Typography
          variant='h2'
          color={palette.text.primary}
          sx={{ fontWeight: 500 }}
        >
          {t('Upon request')}
        </Typography>
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: spaces.xs,
        }}
      >
        <Button
          data-testid='stripe-school-license-cta-button'
          variant='contained'
          color='primary'
          size='large'
          sx={{
            width: '100%',
            fontWeight: 500,
            '&:hover': { color: palette.primary.contrastText },
          }}
          onClick={onContact}
        >
          {t('Request an offer now')}
        </Button>
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: spaces.xl,
        }}
      >
        {display.sections.map((section, sectionIndex) => (
          <Box
            key={sectionIndex}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: spaces.sm,
            }}
          >
            <Typography
              variant='h6'
              color={palette.text.primary}
              sx={{ fontWeight: 600 }}
            >
              {t(section.title)}
            </Typography>
            <Box
              sx={{ display: 'flex', flexDirection: 'column', gap: spaces.xs }}
            >
              {section.features.map((feature, featureIndex) => (
                <Box
                  key={featureIndex}
                  sx={{
                    display: 'flex',
                    gap: spaces.xs,
                    alignItems: 'center',
                  }}
                >
                  <SvgIcon component={getMuiIcon('checkVerify')} />
                  <Typography
                    variant='body2'
                    sx={{
                      color: palette.text.secondary,
                      fontWeight: 300,
                    }}
                  >
                    {t(feature.text)}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default StripeSchoolLicenseCta;
